import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  AccessTime as ClockIcon,
  Description as QuoteIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Close as CloseIcon,
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
import { createApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NotificationDropdown = ({ onNavigateToTask, onViewTaskManagement }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [assignedTickets, setAssignedTickets] = useState([]);
  
  const open = Boolean(anchorEl);

  // Fetch unread count from API
  const fetchUnreadCount = async () => {
    if (!user?.id) return;

    try {
      // Fetch general notification count
      const countResponse = await fetch(createApiUrl(`api/notifications/count`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      let generalCount = 0;
      if (countResponse.ok) {
        const countData = await countResponse.json();
        generalCount = countData.count || 0;
      }

      // Fetch task notifications count (count unread tasks)
      const taskResponse = await fetch(createApiUrl(`api/tasks/user/${user.id}/notifications`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      let taskCount = 0;
      if (taskResponse.ok) {
        const taskData = await taskResponse.json();
        if (taskData.success && Array.isArray(taskData.data)) {
          // Count tasks that are not completed or cancelled
          taskCount = taskData.data.filter(task => 
            !['Completed', 'Cancelled'].includes(task.status_name)
          ).length;
        }
      }

      setUnreadCount(generalCount + taskCount);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  // Fetch notifications from the API (both tasks and general notifications)
  const fetchNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError('');
      
      // Fetch task notifications
      const taskResponse = await fetch(createApiUrl(`api/tasks/user/${user.id}/notifications`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch general notifications (quotes, approvals, etc.)
      console.log('🔔 Fetching general notifications from /api/notifications');
      const generalResponse = await fetch(createApiUrl(`api/notifications?limit=20`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('🔔 General notifications response status:', generalResponse.status);

      try {
        const assignedResponse = await fetch(createApiUrl('api/tickets/assigned/me'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (assignedResponse.ok) {
          const assignedData = await assignedResponse.json();
          setAssignedTickets(Array.isArray(assignedData.tickets) ? assignedData.tickets : []);
        } else {
          setAssignedTickets([]);
        }
      } catch (assignedErr) {
        setAssignedTickets([]);
      }

      const combinedNotifications = [];

      // Process task notifications
      if (taskResponse.ok) {
        const taskData = await taskResponse.json();
        if (taskData.success && Array.isArray(taskData.data)) {
          const transformedTasks = taskData.data.map(task => transformTaskToNotification(task));
          combinedNotifications.push(...transformedTasks);
        }
      }

      // Process general notifications
      if (generalResponse.ok) {
        const generalData = await generalResponse.json();
        if (generalData.success && Array.isArray(generalData.data)) {
          const transformedGeneral = generalData.data.map(notif => transformGeneralNotification(notif));
          combinedNotifications.push(...transformedGeneral);
        }
      }
      
      // Sort by read status, priority, and timestamp
      combinedNotifications.sort((a, b) => {
        // Unread first
        const aUnread = a.is_read === false || a.unread === true;
        const bUnread = b.is_read === false || b.unread === true;
        if (aUnread !== bUnread) {
          return bUnread ? 1 : -1;
        }
        // Then by priority
        const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
        const aPriority = priorityOrder[a.priority] || 3;
        const bPriority = priorityOrder[b.priority] || 3;
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        // Then by timestamp (newest first)
        const aTime = new Date(a.created_at || a.timestamp);
        const bTime = new Date(b.created_at || b.timestamp);
        return bTime - aTime;
      });
      
      setNotifications(combinedNotifications);
      
      // Update unread count based on actual notifications
      const actualUnreadCount = combinedNotifications.filter(n => 
        (n.is_read === false || n.unread === true) && 
        !n.is_dismissed
      ).length;
      setUnreadCount(actualUnreadCount);
      
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // Transform task data into notification format
  const transformTaskToNotification = (task) => {
    const now = new Date();
    const startTime = new Date(task.start_time);
    const isOverdue = startTime < now && !['Completed', 'Cancelled'].includes(task.status_name);
    
    const customerName = task.customer_company_name || `${task.customer_first_name} ${task.customer_last_name}`;
    
    // Determine notification type and message
    let title, message, icon, type;
    
    if (isOverdue) {
      title = 'Overdue Task';
      message = `${task.subject} - Scheduled for ${formatTimestamp(task.start_time)}`;
      icon = <ErrorIcon />;
      type = 'overdue';
    } else if (task.notification_type === 'today') {
      title = 'Task Due Today';
      message = `${task.subject} - ${task.type_name}`;
      icon = <ClockIcon />;
      type = 'today';
    } else if (task.notification_type === 'tomorrow') {
      title = 'Task Due Tomorrow';
      message = `${task.subject} - ${task.type_name}`;
      icon = <ScheduleIcon />;
      type = 'tomorrow';
    } else if (task.notification_type === 'high_priority') {
      title = `${task.priority_name} Priority Task`;
      message = `${task.subject} - ${task.type_name}`;
      icon = <WarningIcon />;
      type = 'high_priority';
    } else {
      title = 'Upcoming Task';
      message = `${task.subject} - ${task.type_name}`;
      icon = <AssignmentIcon />;
      type = 'upcoming';
    }

    return {
      id: `task-${task.id}`,
      taskId: task.id,
      type,
      title,
      message,
      timestamp: formatTimestamp(task.start_time, true),
      priority: task.notification_priority || 'medium',
      icon,
      customer: customerName,
      unread: true, // All fetched notifications are considered unread initially
      task: task, // Store the full task data for navigation
      source: 'task'
    };
  };

  // Transform general notification data
  const transformGeneralNotification = (notif) => {
    let icon;
    
    // Set icon based on notification type
    switch (notif.notification_type) {
      case 'quote_approval':
        icon = <QuoteIcon color="warning" />;
        break;
      case 'quote_approved':
        icon = <ApprovedIcon color="success" />;
        break;
      case 'quote_rejected':
        icon = <RejectedIcon color="error" />;
        break;
      case 'task_assigned':
        icon = <AssignmentIcon />;
        break;
      case 'job_update':
        icon = <BuildIcon />;
        break;
      case 'system_alert':
        icon = <WarningIcon />;
        break;
      case 'web_lead':
        icon = <PersonIcon color="primary" />;
        break;
      case 'ticket_assigned':
      case 'ticket_update':
        icon = <SupportIcon />;
        break;
      default:
        icon = <NotificationsIcon />;
    }

    let title = notif.title;
    if (notif.notification_type === 'web_lead') {
      title = t(notif.metadata?.existing ? 'crm.webLead.returningTitle' : 'crm.webLead.newTitle');
    } else if (['ticket_assigned', 'ticket_update'].includes(notif.notification_type) && notif.metadata?.event) {
      title = t(`tickets.notify.${notif.metadata.event}`, notif.title);
    }

    return {
      id: `notif-${notif.id}`,
      notificationId: notif.id,
      type: notif.notification_type,
      title,
      message: notif.message,
      timestamp: formatTimestamp(notif.created_at, true),
      priority: notif.priority || 'medium',
      icon,
      is_read: notif.is_read,
      related_entity_type: notif.related_entity_type,
      related_entity_id: notif.related_entity_id,
      action_url: notif.action_url,
      metadata: notif.metadata,
      created_at: notif.created_at,
      source: 'general'
    };
  };

  // Format timestamp for display
  // TKT-441: Math.floor on a negative difference rounded -2 hours down to
  // -1 day, so everything from the last 24 hours read "1 day ago". Work
  // with the positive elapsed time instead.
  const formatTimestamp = (dateString, relative = false) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now;
    const absMs = Math.abs(diffMs);
    const minutes = Math.floor(absMs / (1000 * 60));
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const days = Math.floor(absMs / (1000 * 60 * 60 * 24));

    if (relative && diffMs <= 0) {
      if (days > 0) return t('notificationTime.daysAgo', { count: days });
      if (hours > 0) return t('notificationTime.hoursAgo', { count: hours });
      if (minutes > 0) return t('notificationTime.minutesAgo', { count: minutes });
      return t('notificationTime.justNow');
    } else if (relative) {
      if (days > 0) return t('notificationTime.inDays', { count: days });
      if (hours > 0) return t('notificationTime.inHours', { count: hours });
      return t('notificationTime.soon');
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load notifications when component mounts or user changes
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [user?.id]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  // Poll for unread count updates every 30 seconds
  useEffect(() => {
    if (!user?.id) return;
    
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [user?.id]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, unread: false } : n
      )
    );
  };

  const handleMarkAllAsRead = async () => {
    // Mark all general notifications as read in backend
    try {
      await fetch(createApiUrl(`api/notifications/read-all`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
    
    // Update local state
    setNotifications(prev => 
      prev.map(n => ({ ...n, unread: false, is_read: true }))
    );
    
    // Update count
    setUnreadCount(0);
  };

  const handleViewTask = async (notification) => {
    // Mark as read when viewing
    handleMarkAsRead(notification.id);
    
    // Mark as read in backend if it's a general notification
    if (notification.source === 'general' && notification.notificationId && !notification.is_read) {
      try {
        await fetch(createApiUrl(`api/notifications/${notification.notificationId}/read`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        // Update count after marking as read
        fetchUnreadCount();
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }
    
    // Close the dropdown
    handleClose();
    
    // Navigate based on notification source and type
    if (notification.source === 'task' && onNavigateToTask && notification.taskId) {
      onNavigateToTask(notification.taskId, 'view');
    } else if (notification.source === 'general') {
      if (notification.related_entity_type === 'quote' && notification.related_entity_id) {
        const params = new URLSearchParams({
          quoteId: notification.related_entity_id,
          action: notification.type === 'quote_approval' ? 'approval' : 'view'
        });
        navigate(`/dashboard/quotes?${params.toString()}`);
      } else if (notification.related_entity_type === 'job' && notification.related_entity_id) {
        navigate(`/dashboard/jobs/${notification.related_entity_id}`);
      } else if (notification.related_entity_type === 'customer' && notification.related_entity_id) {
        navigate(`/dashboard/customers/${notification.related_entity_id}`);
      } else if (notification.related_entity_type === 'ticket' && notification.related_entity_id) {
        navigate(`/dashboard/tickets/${notification.related_entity_id}`);
      } else if (notification.related_entity_type === 'task' && notification.related_entity_id) {
        if (onNavigateToTask) {
          onNavigateToTask(notification.related_entity_id, 'view');
        }
      } else if (notification.action_url) {
        window.location.href = notification.action_url;
      } else {
        navigate('/dashboard');
      }
    }
  };

  // Get button text based on notification type
  const getActionButtonText = (notification) => {
    if (notification.source === 'task') {
      return 'View Task';
    } else if (notification.source === 'general') {
      if (notification.related_entity_type === 'quote') {
        return 'View Quote';
      } else if (notification.related_entity_type === 'job') {
        return 'View Job';
      } else if (notification.related_entity_type === 'task') {
        return 'View Task';
      } else if (notification.related_entity_type === 'customer') {
        return t('crm.webLead.viewCustomer');
      } else if (notification.related_entity_type === 'ticket') {
        return t('tickets.notify.viewTicket');
      } else if (notification.type === 'quote_approval') {
        return 'Review Quote';
      } else if (notification.type === 'quote_approved' || notification.type === 'quote_rejected') {
        return 'View Quote';
      }
    }
    return 'View Details';
  };


  const handleDismissNotification = async (notification, e) => {
    e.stopPropagation();
    
    if (notification.source === 'general' && notification.notificationId) {
      try {
        await fetch(createApiUrl(`api/notifications/${notification.notificationId}/dismiss`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      } catch (err) {
        console.error('Error dismissing notification:', err);
      }
    } else {
      // For task notifications, just remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }
  };

  const handleNotificationClick = async (notification) => {
    // Just mark as read without closing the dropdown
    if (notification.source === 'general' && notification.notificationId && !notification.is_read) {
      try {
        await fetch(createApiUrl(`api/notifications/${notification.notificationId}/read`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Update local state
        setNotifications(prev => 
          prev.map(n => 
            n.id === notification.id ? { ...n, is_read: true, unread: false } : n
          )
        );
      } catch (err) {
        console.error('Error marking notification as read:', err);
      }
    }
    
    handleMarkAsRead(notification.id);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#1976d2';
      case 'low': return '#388e3c';
      default: return '#666';
    }
  };

  const getPriorityChipColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  return (
    <>
      <IconButton 
        color="inherit" 
        onClick={handleClick}
        sx={{ 
          mr: { xs: 0.5, sm: 1 },
          display: { xs: 'none', sm: 'inline-flex' }
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              width: 420,
              maxHeight: 600,
              borderRadius: 2,
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              border: '1px solid rgba(0,0,0,0.08)'
            }
          }
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000062' }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button 
                size="small" 
                onClick={handleMarkAllAsRead}
                sx={{ fontSize: '0.75rem', textTransform: 'none' }}
              >
                Mark all as read
              </Button>
            )}
          </Box>
          {unreadCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {unreadCount} new notification{unreadCount !== 1 ? 's' : ''} requiring attention
            </Typography>
          )}
        </Box>

        {assignedTickets.length > 0 && (
          <Box
            sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,98,0.03)' }}
            role="region"
            aria-label={t('tickets.notify.myOpen', { count: assignedTickets.length })}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#000062', mb: 0.5 }}>
              {t('tickets.notify.myOpen', { count: assignedTickets.length })}
            </Typography>
            {assignedTickets.slice(0, 5).map((ticket) => (
              <Button
                key={ticket.id}
                fullWidth
                size="small"
                onClick={() => {
                  handleClose();
                  navigate(`/dashboard/tickets/${ticket.id}`);
                }}
                aria-label={t('tickets.notify.openTicketLabel', { id: ticket.id, name: ticket.name })}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0.5, color: 'text.primary' }}
                startIcon={<SupportIcon fontSize="small" sx={{ color: '#000062' }} />}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                  {`#${ticket.id} ${ticket.name || ''}`}
                  {ticket.customer_name ? ` · ${ticket.customer_name}` : ''}
                </Box>
              </Button>
            ))}
          </Box>
        )}

        {/* Notifications List */}
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Loading notifications...
              </Typography>
            </Box>
          )}
          
          {error && !loading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {error}
              </Typography>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={fetchNotifications}
                sx={{ fontSize: '0.75rem' }}
              >
                Retry
              </Button>
            </Box>
          )}
          
          {!loading && !error && notifications.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No notifications
              </Typography>
              <Typography variant="caption" color="text.secondary">
                You're all caught up! No pending tasks require immediate attention.
              </Typography>
            </Box>
          )}
          
          {!loading && !error && notifications.map((notification, index) => (
            <Box key={notification.id}>
              <MenuItem
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  p: 0,
                  display: 'block',
                  backgroundColor: notification.unread || !notification.is_read ? 'rgba(0, 0, 98, 0.02)' : 'transparent',
                  '&:hover': {
                    backgroundColor: notification.unread || !notification.is_read ? 'rgba(0, 0, 98, 0.04)' : 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                <Box sx={{ p: 2, position: 'relative' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    {/* Icon */}
                    <Box 
                      sx={{ 
                        color: getPriorityColor(notification.priority),
                        mt: 0.25,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {notification.icon}
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0, pr: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: notification.unread || !notification.is_read ? 'bold' : 'medium',
                            color: notification.unread || !notification.is_read ? '#000062' : 'text.primary',
                            pr: 1
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Chip 
                          label={notification.priority.toUpperCase()} 
                          size="small"
                          color={getPriorityChipColor(notification.priority)}
                          sx={{ 
                            height: 20, 
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}
                        />
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {notification.message}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {notification.timestamp}
                        </Typography>
                        {notification.customer && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#000062', 
                              backgroundColor: 'rgba(0,0,98,0.08)',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 'medium'
                            }}
                          >
                            {notification.customer}
                          </Typography>
                        )}
                      </Box>

                      {/* Action Button */}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                        <Button 
                          size="small" 
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTask(notification);
                          }}
                          sx={{ 
                            fontSize: '0.7rem',
                            textTransform: 'none',
                            px: 2,
                            py: 0.5,
                            borderRadius: 1
                          }}
                        >
                          {getActionButtonText(notification)}
                        </Button>
                      </Box>
                    </Box>

                    {/* Unread indicator */}
                    {(notification.unread || !notification.is_read) && (
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: '#000062',
                          mt: 1,
                          flexShrink: 0
                        }} 
                      />
                    )}
                  </Box>
                  
                  {/* Dismiss button */}
                  <Tooltip title="Dismiss notification">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDismissNotification(notification, e)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'error.main',
                          backgroundColor: 'rgba(211, 47, 47, 0.08)'
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </MenuItem>
              {index < notifications.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button 
            fullWidth 
            variant="text" 
            onClick={() => {
              handleClose();
              // Navigate to the Task Management page. Previously this button
              // only closed the dropdown (dead control).
              if (onViewTaskManagement) {
                onViewTaskManagement();
              }
            }}
            sx={{ 
              textTransform: 'none',
              fontSize: '0.875rem',
              color: '#000062',
              fontWeight: 'medium'
            }}
          >
            View Task Management
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationDropdown; 